/**
 * Field Type Extractor Utility Functions
 * 
 * Collection of utility functions for analyzing and extracting metadata about form fields
 * throughout the MacSafety web application. These functions go beyond standard TypeScript
 * types to provide rich metadata that helps dictate display behavior in form views and
 * form rendering components.
 * 
 * Key Functions:
 * - createFormFieldMetadata: Analyzes form classes to create comprehensive field metadata
 * - getFieldTypeMetadata: Runtime utility for checking individual field types
 * 
 * Field Type Metadata Includes:
 * - isUploadArray: Whether the field is an array of file uploads
 * - isSignatureArray: Whether the field is an array of signatures
 * - isDate: Whether the field contains date/time data
 * - isString: Whether the field contains string data
 * - isNumber: Whether the field contains numeric data
 * - isBoolean: Whether the field contains boolean data
 * 
 * Primary Use Cases:
 * - Form rendering components for proper field display
 * - Form view components for data presentation
 * - Form validation for type-specific validation rules
 * - Dynamic form generation based on field metadata
 * - Attachment and signature field identification
 * 
 * Components and Services that utilize these functions:
 * - FormRenderService: For determining how to render different field types
 * - FormViewComponent: For displaying form data with proper formatting
 * - Form submission components: For validation and processing
 * - Any component requiring field type analysis
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

/**
 * Type-safe field type metadata interface
 * Defines the structure for field type information beyond standard TypeScript types
 * Used to determine how fields should be displayed and processed in forms
 */
export interface FieldTypeMetadata {
  isUploadArray: boolean;    // Field contains array of file uploads
  isSignatureArray: boolean; // Field contains array of signatures
  isDate: boolean;           // Field contains date/time data
  isString: boolean;         // Field contains string data
  isNumber: boolean;         // Field contains numeric data
  isBoolean: boolean;        // Field contains boolean data
}

/**
 * Form field metadata lookup interface
 * Maps field names to their type metadata for easy lookup and access
 * Used throughout the application for field type determination
 */
export interface FormFieldMetadata {
  [fieldName: string]: FieldTypeMetadata;
}

/**
 * Creates comprehensive form field metadata by analyzing the form class and its attachment requirements
 * This function properly distinguishes uploads from signatures using the form's configuration
 * and provides rich metadata for form rendering and display purposes
 * 
 * @param FormClass - The form class constructor to analyze
 * @returns FormFieldMetadata object containing type information for all form fields
 */
export function createFormFieldMetadata(FormClass: any): FormFieldMetadata {
  const metadata: FormFieldMetadata = {};
  
  // Get form info to access attachment requirements from form configuration
  const formInfo = FormClass.getFormInfo();
  const attachmentRequirements = formInfo.formAttachmentRequirements;
  
  // Create sets for quick lookup of upload and signature field identifiers
  const uploadFields = new Set(
    attachmentRequirements.uploads?.map((req: any) => req.identifier) || []
  );
  const signatureFields = new Set(
    attachmentRequirements.signatures?.map((req: any) => req.identifier) || []
  );
  
  // Create an instance to analyze the class structure and property types
  const instance = new FormClass();
  
  // Analyze each property in the form instance
  Object.keys(instance).forEach(key => {
    const value = instance[key];
    
    // Create comprehensive metadata for each field
    metadata[key] = {
      isUploadArray: uploadFields.has(key),
      isSignatureArray: signatureFields.has(key),
      isDate: value instanceof Date || key.toLowerCase().includes('date'),
      isString: typeof value === 'string',
      isNumber: typeof value === 'number',
      isBoolean: typeof value === 'boolean'
    };
  });
  
  return metadata;
}

/**
 * Runtime utility to check field types for individual fields
 * This is a fallback for cases where you need to check a single field
 * without creating the full form metadata
 * 
 * @param value - The field value to analyze
 * @param fieldName - The name of the field for context
 * @param uploadFields - Set of field names that are upload arrays
 * @param signatureFields - Set of field names that are signature arrays
 * @returns FieldTypeMetadata for the specified field
 */
export function getFieldTypeMetadata(
  value: any,
  fieldName: string,
  uploadFields: Set<string> = new Set(),
  signatureFields: Set<string> = new Set()
): FieldTypeMetadata {
  return {
    isUploadArray: uploadFields.has(fieldName),
    isSignatureArray: signatureFields.has(fieldName),
    isDate: value instanceof Date || fieldName.toLowerCase().includes('date'),
    isString: typeof value === 'string',
    isNumber: typeof value === 'number',
    isBoolean: typeof value === 'boolean'
  };
}